import config from 'appConfig';
import datetime from 'lib/datetime';
import { WORKDAYS_TO_ADD } from './constants';
import {
  BentoDetails,
  BuffetDetails,
  CanapesDetails,
  CateringGA4Item,
  MiniBentoDetails,
} from './types';
import { LABEL_BY_KEY_BUFFET_TYPE } from './buffet';
import { LABEL_BY_BENTO_TYPE } from './bento';
import { slugify } from 'lib/core';
import { CANAPES_STATIC_DATA } from './canapes';
import { LABEL_BY_MINI_BENTO_TYPE } from './mini-bento';

export const numberToWords = (n: number): string => {
  if (n < 0 || n > 99 || !Number.isInteger(n)) {
    throw new Error('Only supports integer from 0 to 99');
  }

  const units = [
    'Zero',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];

  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];

  if (n < 20) return units[n];

  const ten = Math.floor(n / 10);
  const unit = n % 10;

  return unit === 0 ? tens[ten] : `${tens[ten]}-${units[unit]}`;
};

export const formatPrice = (value: number): string =>
  Number.isInteger(value) ? value.toString() : value.toFixed(2);

export const countNextWorkdays = (
  workdaysToAdd: number = WORKDAYS_TO_ADD,
) => {
  const startDate = datetime().startOf('day');

  let addedWorkdays = 0;
  let calendarDays = 0;

  while (addedWorkdays < workdaysToAdd) {
    const currentDay = startDate.add(calendarDays, 'day');
    const dayOfWeek = currentDay.day();

    // Skip 0: Sunday, 6: Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      addedWorkdays++;
    }

    calendarDays++;
  }

  return calendarDays;
};

export const formatDeliveryDatetime = ({
  date,
  time,
  locale = config.locale,
  timezone = config.timezone,
}: {
  date?: string;
  time?: string;
  locale?: string;
  timezone?: string;
}): {
  dateFormatted: string;
  timeFormatted: string;
  dateTimeUtc: string;
} => {
  if (!date || !time) {
    return {
      dateFormatted: 'N/A',
      timeFormatted: 'N/A',
      dateTimeUtc: '',
    };
  }

  const dateFormatted = datetime(date).format('YYYY-MM-DD');
  const dateTimeFormatted = `${dateFormatted}T${time}`;

  const dateTime = datetime
    .tz(dateTimeFormatted, timezone)
    .locale(locale);

  return {
    dateFormatted: dateTime.format('DD MMMM YYYY'),
    timeFormatted: dateTime.format('hh:mm A'),
    dateTimeUtc: dateTime.utc().toISOString(),
  };
};

export const setNullIfEmpty = (
  value: string | undefined,
): string | null => {
  if (typeof value === 'undefined' || value === '') {
    return null;
  }

  return value;
};

export const flattenCateringItems = (
  buffet?: BuffetDetails,
  bento?: BentoDetails,
  miniBento?: MiniBentoDetails,
  canapes?: CanapesDetails,
): CateringGA4Item[] => {
  const items: CateringGA4Item[] = [
    ...(buffet
      ? [
          {
            item_id: slugify(LABEL_BY_KEY_BUFFET_TYPE[buffet.type]),
            item_name: 'Buffet',
            item_category: LABEL_BY_KEY_BUFFET_TYPE[buffet.type],
            price: buffet.price_per_pax,
            quantity: buffet.total_pax,
          },
          ...buffet.addons.map((addon) => ({
            item_id: `addon-${addon.type}-${slugify(addon.name)}}`,
            item_name: addon.name,
            ...(addon.additional_price
              ? { price: addon.additional_price }
              : {}),
            item_quantity: addon.quantity,
            item_category: 'Buffet',
            item_category2: 'Addon',
            item_category3:
              addon.type.charAt(0).toUpperCase() +
              addon.type.slice(1),
          })),
        ]
      : []),
    ...(bento
      ? bento.items.map((bento) => ({
          item_id: slugify(bento.name),
          item_name: bento.name,
          item_category: 'Bento',
          item_category2: LABEL_BY_BENTO_TYPE[bento.type],
          price: bento.price,
          quantity: bento.quantity,
        }))
      : []),
    ...(miniBento
      ? miniBento.items.map((item) => ({
          item_id: slugify(item.name),
          item_name: item.name,
          item_category: 'Mini Bento',
          item_category2: LABEL_BY_MINI_BENTO_TYPE[item.type],
          price: item.price,
          quantity: item.quantity,
        }))
      : []),
    ...(canapes
      ? canapes.items.map((canape) => ({
          item_id: slugify(canape.name),
          item_name: canape.name,
          item_category: 'Canapés',
          price:
            canape.price / CANAPES_STATIC_DATA.servingSize.halfBox,
          quantity: canape.quantity,
        }))
      : []),
  ];

  return items;
};

export const removeSessionStorageByPrefix = (prefix: string) => {
  const keysToRemove: string[] = [];

  for (let i = 0; i < window.sessionStorage.length; i++) {
    const key = window.sessionStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }

  if (keysToRemove.length) {
    keysToRemove.forEach((key) => {
      window.sessionStorage.removeItem(key);
    });
  }
};
